import { useEffect, useState } from "react";

/**
 * useIsMobile — true when viewport is below `breakpoint` (default 768px,
 * matching Tailwind's `md` breakpoint). Listens to `resize` so orientation
 * changes update the value. Returns `false` on the very first render even
 * on mobile to keep SSR / hydration deterministic — this is fine here
 * because the landing page is CSR-only and `useEffect` fires on mount.
 */
export default function useIsMobile(breakpoint = 768) {
  // Initialize synchronously from `window` so the first render matches the
  // real viewport → no visual flash from "desktop → mobile" swap on load.
  // Guards against SSR by checking for window.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches;
  });

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const update = () => setIsMobile(mq.matches);
    update();
    // Safari <14 uses addListener; modern uses addEventListener.
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, [breakpoint]);

  return isMobile;
}
